import React, { useEffect } from 'react';
import { motion } from 'framer-motion';
import { BrainCircuit, CheckCircle2, ArrowRight, FileText } from 'lucide-react';
import { useNavigate, useLocation } from 'react-router-dom';
import { useSound } from '../hooks/useSound';

const springConfig = {
  type: "spring",
  stiffness: 50,
  damping: 20,
  mass: 1
};

export const ThankYou = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const { playHover, playClick } = useSound();
  const formData = location.state?.formData;

  useEffect(() => {
    // If no form data, redirect to consultation
    if (!formData) {
      navigate('/consultation');
    }
  }, [formData, navigate]);

  if (!formData) return null;

  return (
    <div className="pt-24 px-4 min-h-screen">
      <div className="max-w-4xl mx-auto">
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 1, ...springConfig }}
          className="text-center mb-12"
        >
          <div className="flex justify-center mb-6">
            <motion.div
              whileHover={{ scale: 1.05 }}
              transition={springConfig}
              className="relative"
            >
              <CheckCircle2 className="w-16 h-16 text-green-400" />
              <motion.div
                animate={{
                  scale: [1, 1.1, 1],
                  opacity: [0.5, 0.8, 0.5],
                }}
                transition={{
                  duration: 5,
                  repeat: Infinity,
                  ease: "easeInOut",
                }}
                className="absolute inset-0 bg-green-400/10 rounded-full filter blur-2xl"
              />
            </motion.div>
          </div>
          <h1 className="text-4xl font-bold mb-4">
            <span className="bg-gradient-to-r from-amber-200 to-amber-400 text-transparent bg-clip-text">
              Thank You, {formData.name}!
            </span>
          </h1>
          <p className="text-gray-400 text-lg max-w-2xl mx-auto">
            We've received your consultation request and will be in touch soon.
          </p>
        </motion.div>

        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.2, duration: 1, ...springConfig }}
          className="space-y-8"
        >
          <div className="p-6 bg-gradient-to-b from-amber-400/5 to-transparent rounded-xl border border-amber-400/10 backdrop-blur-sm">
            <h2 className="text-2xl font-bold text-white mb-6">Next Steps</h2>
            <div className="space-y-4">
              <div className="flex items-start gap-4">
                <div className="w-8 h-8 bg-amber-400/10 rounded-lg flex items-center justify-center flex-shrink-0">
                  <FileText className="w-5 h-5 text-amber-400" />
                </div>
                <div>
                  <h3 className="text-lg font-semibold text-white mb-2">What to Expect</h3>
                  <p className="text-gray-400 mb-4">
                    Our team will review your information and reach out to you within 24 hours to schedule your consultation.
                  </p>
                </div>
              </div>
            </div>
          </div>

          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.4, duration: 1, ...springConfig }}
            className="p-6 bg-gradient-to-b from-amber-400/5 to-transparent rounded-xl border border-amber-400/10 backdrop-blur-sm"
          >
            <h2 className="text-2xl font-bold text-white mb-6">Preparing for Your Consultation</h2>
            <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
              <div className="space-y-2">
                <div className="text-2xl font-bold text-amber-400">01</div>
                <h3 className="text-lg font-semibold text-white">Business Goals</h3>
                <p className="text-gray-400">Consider your key business objectives and challenges</p>
              </div>
              <div className="space-y-2">
                <div className="text-2xl font-bold text-amber-400">02</div>
                <h3 className="text-lg font-semibold text-white">Current Metrics</h3>
                <p className="text-gray-400">Gather any relevant business performance data</p>
              </div>
              <div className="space-y-2">
                <div className="text-2xl font-bold text-amber-400">03</div>
                <h3 className="text-lg font-semibold text-white">Questions</h3>
                <p className="text-gray-400">Prepare any specific questions about our services</p>
              </div>
            </div>
          </motion.div>
        </motion.div>

        <motion.div
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          transition={{ delay: 0.6, duration: 1 }}
          className="mt-8 text-center"
        >
          <div className="flex items-center justify-center gap-2 text-gray-400">
            <BrainCircuit className="w-5 h-5 text-amber-400" />
            <span>Powered by ProfitIQ.AI</span>
          </div>
        </motion.div>
      </div>
    </div>
  );
};